/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../app/nls/StringResources',
	'../../../lib/@waca/dashboard-common/dist/utils/TextEditorConstants'
], function (resources, TextEditorConstants) {
	'use strict';

	class TabWidgetFontFamilyPropertiesProvider {
		constructor(options) {
			this.features = options.features;
			this.features['Properties'].registerProvider(this);
			this._content = options.content;
			this._dashboard = this.features['Dashboard.API'];
			const featureChecker = this._dashboard.getGlassCoreSvc('.FeatureChecker');
			this.isPropertiesV2Enabled = featureChecker && featureChecker.checkValue('dashboard', 'PropertiesV2', 'enabled');
		}

		get FONT_FAMILY_SECTIONID() {
			return (this.isPropertiesV2Enabled ? 'tabs' : 'general.tabs') + '.masterTabStyles.fontFamily.fontFamilyLine.left';
		}

		get FONT_FAMILY_SUBCLASS_SECTIONID() {
			return (this.isPropertiesV2Enabled ? 'tabs' : 'general.tabs') + '.masterTabStyles.fontFamily.fontFamilyLine.right';
		}

		_getLayoutModel() {
			return this.features['Models.internal'].getLayoutModel();
		}

		_isSupported() {
			return !!this._content.getContainer();
		}

		_getPropertyValue(name) {
			const defaultValue = name === 'tabFontFamily' ? 'ibmplexsans' : 'regular';
			return this._getLayoutModel().get(name) || defaultValue;
		}

		_setPropertyValue(name, value) {
			const oldValue = this._getPropertyValue(name);
			if (!oldValue || oldValue !== value) {
				this._getLayoutModel().set({
					[name]: value
				}, {
					payloadData: {
						skipUndoRedo: true
					}
				});
			}
		}

		getPropertyList() {
			const propertyList = [];
			if (this._isSupported()) {
				const selectedCssClass = this._getPropertyValue('tabFontFamily');
				const cssClass = TextEditorConstants.FONT_FAMILY_CHOICES.find(choice => choice.value === selectedCssClass);
				const selectedCssSubClass = this._getPropertyValue('tabFontFamilySubClass');
				const cssSubClass = cssClass.subCssClasses && cssClass.subCssClasses.find(choice => choice.value === selectedCssSubClass);
				propertyList.push(...[{
					id: 'tabFontFamily',
					editor: {
						sectionId: this.FONT_FAMILY_SECTIONID,
						position: 1,
						uiControl: {
							type: 'DropDown',
							ariaDescribedby: resources.get('textFontFamily'),
							options: TextEditorConstants.FONT_FAMILY_CHOICES,
							responsive: false,
							style: 'width: 156px;',
							value: selectedCssClass
						}
					},
					getPropertyValue: this._getPropertyValue.bind(this, 'tabFontFamily'),
					setPropertyValue: (value) => {
						this._setPropertyValue('tabFontFamily', value);
						// Hide tabFontFamilySubClass dropdown when it should not be available
						this._dashboard.triggerDashboardEvent('properties:refreshPane');
					}
				}, {
					id: 'tabFontFamilySubClass',
					editor: {
						sectionId: this.FONT_FAMILY_SUBCLASS_SECTIONID,
						position: 2,
						hidden: !cssClass.subCssClasses || cssClass.subCssClasses.length === 0,
						uiControl: {
							type: 'DropDown',
							ariaDescribedby: resources.get('textFontFamily'),
							options: cssClass.subCssClasses,
							value: cssSubClass && cssSubClass.value,
							style: 'width:80px;',
							responsive: false
						}
					},
					getPropertyValue: this._getPropertyValue.bind(this, 'tabFontFamilySubClass'),
					setPropertyValue: this._setPropertyValue.bind(this, 'tabFontFamilySubClass')
				}]);
			}
			return propertyList;
		}

		getPropertyLayoutList() {
			const layoutList = [];
			if (this._isSupported()) {
				layoutList.push(...[{
					id: 'fontFamily',
					type: 'Section',
					collapsible: false,
					label: resources.get('textFontFamily'),
					position: 1
				}, {
					id: 'fontFamilyLine',
					type: 'Split',
					items: [{
						id: 'left',
						align: 'left',
						items: []
					}, {
						id: 'right',
						align: 'right',
						items: []
					}]
				}]);
			}
			return layoutList;
		}
	}

	return TabWidgetFontFamilyPropertiesProvider;
});